import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getJobDetailsUrl } from '../../../utils/urlHelpers';
import Toast from '../../../components/ui/Toast';
import { SupabaseError } from '../../../utils/errorHandling';
// import { format } from 'date-fns';
import { Badge } from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
// import { Pagination } from '../../../components/ui/Pagination';
// import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
// import { IconButton } from '../../../components/ui/IconButton';
import realtimeRelationshipService from '../../../services/realtimeRelationshipService';
import { useAuth } from '../../../hooks/useAuth';

const JobsTable = ({ 
  jobs = [], 
  onEditJob, 
  onViewApplications, 
  onDeleteJob,
  onDuplicateJob,
  onToggleStatus,
  onRefreshPosting,
  loading = false,
  onCreateJob,
  viewMode = 'table'
}) => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const handleJobTitleClick = useCallback((job) => {
    if (job?.status === 'published' || job?.status === 'active') {
      navigate(getJobDetailsUrl(job));
    } else {
      onEditJob?.(job?.id);
    }
  }, [navigate, onEditJob]);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);
  const [pendingBulkAction, setPendingBulkAction] = useState(null);
  const [currentAction, setCurrentAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [jobsState, setJobsState] = useState(jobs || []);
  const relationshipHandlerIdRef = useRef(null);
  const [animatedJobIds, setAnimatedJobIds] = useState([]);
  const [animatedCountIds, setAnimatedCountIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);

  // Function to update job application count when a real-time event is received
  // Wrapped in useCallback to prevent unnecessary re-renders
  const updateJobApplicationCount = useCallback((jobId) => {
    // Check if the job exists in our state
    const jobExists = jobsState.some(job => job.id === jobId);
    
    if (!jobExists) {
      return; // Don't proceed if job doesn't exist
    }
    
    // Add job ID to animated lists to trigger animations
    setAnimatedJobIds(prev => [...prev, jobId]);
    setAnimatedCountIds(prev => [...prev, jobId]);
    
    // Update the job application count
    setJobsState(prevJobs => {
      return prevJobs.map(job => {
        if (job.id === jobId) {
          // Consistently increment applications_count (database field name)
          const currentCount = job.applications_count || job.application_count || 0;
          const newCount = currentCount + 1;
          
          return {
            ...job,
            applications_count: newCount,
            application_count: newCount, // Keep both for backward compatibility
            newApplications: (job.newApplications || 0) + 1
          };
        }
        return job;
      });
    });
    
    // Remove job ID from animated lists after animation duration
    setTimeout(() => {
      setAnimatedJobIds(prev => prev.filter(id => id !== jobId));
    }, 2000); // Match the row highlight animation duration
    
    setTimeout(() => {
      setAnimatedCountIds(prev => prev.filter(id => id !== jobId));
    }, 800); // Match the count update animation duration
  }, [jobsState]); // Add jobsState as a dependency

  // Update local jobs state when props change
  useEffect(() => {
    setJobsState(jobs);
  }, [jobs]);

  // Setup real-time subscription for job applications
  useEffect(() => {
    // Initialize the real-time service if not already initialized
    if (!realtimeRelationshipService.isInitialized) {
      realtimeRelationshipService.initialize();
    }

    // Register event handler for relationship changes
    const handlerId = realtimeRelationshipService.onRelationshipChange('relationship_changed', (data) => {
      if (data.type === 'job_application') {
        // Extract jobId from metadata if it exists there
        const jobId = data.metadata?.jobId || data.jobId;
        // Update the job application count for the affected job
        updateJobApplicationCount(jobId);
      }
    });

    // Store handler ID in ref instead of state to avoid re-renders
    relationshipHandlerIdRef.current = handlerId;

    // Cleanup function
    return () => {
      if (relationshipHandlerIdRef.current) {
        realtimeRelationshipService.removeEventHandler('relationship_changed', relationshipHandlerIdRef.current);
      }
    };
  }, [updateJobApplicationCount]); // Add updateJobApplicationCount as a dependency

  // Filter and sort jobs
  const filteredJobs = useMemo(() => {
    let filtered = jobsState.filter(job => {
      const matchesSearch = job?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           job?.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           job?.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           job?.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           job?.location?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || job?.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    // Sort jobs
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b?.created_at || b?.updated_at || 0) - new Date(a?.created_at || a?.updated_at || 0);
        case 'oldest':
          return new Date(a?.created_at || a?.updated_at || 0) - new Date(b?.created_at || b?.updated_at || 0);
        case 'applications':
          return (b?.applications || 0) - (a?.applications || 0);
        case 'title':
          return (a?.title || '').localeCompare(b?.title || '');
        case 'expiring':
          return new Date(a?.application_deadline || a?.expiresAt || '9999-12-31') - new Date(b?.application_deadline || b?.expiresAt || '9999-12-31');
        default:
          return 0;
      }
    });

    return filtered;
  }, [jobsState, searchQuery, statusFilter, sortBy]);
  
  // Paginated jobs
  const paginatedJobs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredJobs.slice(startIndex, endIndex);
  }, [filteredJobs, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);

  const handleSelectJob = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleSelectAll = () => {
    if (selectedJobs.length === filteredJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(job => job?.id).filter(Boolean));
    }
  };

  const handleBulkAction = (action) => {
    setPendingBulkAction(action);
    setShowBulkConfirm(true);
  };

  const confirmBulkAction = async () => {
    setActionLoading('bulk');
    try {
      // Process bulk actions sequentially for better error handling
      for (const jobId of selectedJobs) {
        switch (pendingBulkAction) {
          case 'activate': 
            await onToggleStatus?.(jobId, 'published');
            // Update local state
            setJobsState(prevJobs => prevJobs.map(job => 
              job.id === jobId ? { ...job, status: 'published' } : job
            ));
            break;
          case 'close': 
            await onToggleStatus?.(jobId, 'closed');
            // Update local state
            setJobsState(prevJobs => prevJobs.map(job => 
              job.id === jobId ? { ...job, status: 'closed' } : job
            ));
            break;
          case 'delete': {
            const deleteResult = await onDeleteJob?.(jobId);
            if (deleteResult?.success !== false) {
              setJobsState(prevJobs => prevJobs.filter(job => job.id !== jobId));
            }
            break;
          }
          default:
            break;
        }
      }
    } catch (e) {
      const friendly = e instanceof SupabaseError
        ? e.getUserFriendlyMessage()
        : new SupabaseError(e?.message || 'Bulk action failed', e).getUserFriendlyMessage();
      console.error('[JobsTable] Bulk action error:', e);
      setToast({ show: true, message: friendly, type: 'error' });
    } finally {
      setActionLoading(null);
      setSelectedJobs([]);
      setShowBulkActions(false);
      setShowBulkConfirm(false);
    }
  };

  // Handle individual job deletion confirmation
  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;
    
    setActionLoading(jobToDelete);
    setCurrentAction('delete');
    try {
      const result = await onDeleteJob?.(jobToDelete);
      // Only remove from local state if the operation succeeded;
      // parent's loadDashboardData will sync the authoritative state.
      if (result?.success !== false) {
        setJobsState(prevJobs => prevJobs.filter(job => job.id !== jobToDelete));
      }
    } catch (e) {
      const friendly = e instanceof SupabaseError
        ? e.getUserFriendlyMessage()
        : new SupabaseError(e?.message || 'Failed to delete job', e).getUserFriendlyMessage();
      console.error('[JobsTable] Delete job error:', e);
      setToast({ show: true, message: friendly, type: 'error' });
    } finally {
      setActionLoading(null);
      setCurrentAction(null);
      setShowDeleteConfirm(false);
      setJobToDelete(null);
    }
  };
  
  // Handle job status toggle with proper UI sync
  const handleStatusToggle = useCallback(async (jobId, newStatus) => {
    setActionLoading(jobId);
    try {
      await onToggleStatus(jobId, newStatus);
      
      // Update local state to reflect the status change
      setJobsState(prevJobs => prevJobs.map(job => {
        if (job.id === jobId) {
          // Reset newApplications count if job is being archived or closed
          // This ensures the badge count is properly synchronized with job status
          const resetNewApplications = ['archived', 'closed'].includes(newStatus);
          
          return { 
            ...job, 
            status: newStatus,
            // Reset newApplications count if job is being archived or closed
            newApplications: resetNewApplications ? 0 : job.newApplications
          };
        }
        return job;
      }));
      
      // Add to animated jobs to provide visual feedback
      setAnimatedJobIds(prev => [...prev, jobId]);
      
      // Remove job ID from animated list after animation duration
      setTimeout(() => {
        setAnimatedJobIds(prev => prev.filter(id => id !== jobId));
      }, 2000); // Match the row highlight animation duration
      
    } catch {
      // Silent error handling for status updates
    } finally {
      setActionLoading(null);
    }
  }, [onToggleStatus]);
  
  // Handle individual job actions
  const handleDropdownAction = async (action, jobId) => {
    if (!jobId) return;
    
    setActionLoading(jobId);
    setCurrentAction(action);
    try {
      switch (action) {
        case 'edit':
          onEditJob?.(jobId);
          break;
        case 'clone':
          if (typeof onDuplicateJob === 'function') {
            await onDuplicateJob(jobId);
          }
          break;
        case 'delete':
          setJobToDelete(jobId);
          setShowDeleteConfirm(true);
          return;
        case 'refresh':
          if (typeof onRefreshPosting === 'function') {
            await onRefreshPosting(jobId);
          }
          setAnimatedJobIds(prev => [...prev, jobId]);
          setTimeout(() => {
            setAnimatedJobIds(prev => prev.filter(id => id !== jobId));
          }, 2000);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error during action ${action}:`, error);
    } finally {
      setActionLoading(null);
      setCurrentAction(null);
    }
  };

  const getStatusIcon = (status, moderationStatus) => {
    // Prioritize moderation status for pending/evaluation_needed jobs
    if (moderationStatus === 'pending') {
      return 'Clock';
    }
    if (moderationStatus === 'evaluation_needed') {
      return 'AlertTriangle';
    }
    
    switch (status) {
      case 'published':
      case 'active':
        return 'CheckCircle';
      case 'paused':
        return 'Pause';
      case 'draft':
        return 'Edit';
      case 'closed':
        return 'Pause';
      case 'expired':
        return 'Clock';
      case 'filled':
        return 'UserCheck';
      case 'archived':
        return 'Archive';
      default:
        return 'Circle';
    }
  };
  
  const getStatusVariant = (status, moderationStatus) => {
    // Prioritize moderation status for pending/evaluation_needed jobs
    if (moderationStatus === 'pending') {
      return 'warning';
    }
    if (moderationStatus === 'evaluation_needed') {
      return 'warning';
    }
    
    switch (status?.toLowerCase()) {
      case 'published':
      case 'active':
        return 'success';
      case 'draft':
      case 'archived':
        return 'default';
      case 'closed':
        return 'warning';
      case 'expired':
        return 'error';
      case 'filled':
        return 'info';
      default:
        return 'default';
    }
  };
  
  const getStatusColor = (status, moderationStatus) => {
    // Prioritize moderation status for pending/evaluation_needed jobs
    if (moderationStatus === 'pending') {
      return 'bg-warning-100 text-warning-700';
    }
    if (moderationStatus === 'evaluation_needed') {
      return 'bg-orange-100 text-orange-700';
    }
    
    switch (status?.toLowerCase()) {
      case 'published':
      case 'active':
        return 'bg-success-100 text-success-700';
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      case 'archived':
        return 'bg-gray-100 text-gray-700';
      case 'closed':
        return 'bg-warning-100 text-warning-700';
      case 'expired':
        return 'bg-red-100 text-red-700';
      case 'filled':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Helper to get display status (prioritize moderation status)
  const getDisplayStatus = (status, moderationStatus, job = null) => {
    if (moderationStatus === 'pending') {
      return 'Pending Review';
    }
    if (moderationStatus === 'evaluation_needed') {
      return 'Under Review';
    }
    // Check auto-approved: moderated_by is null and comment contains 'Auto-approved'
    if (
      moderationStatus === 'approved' &&
      job?.moderated_by === null &&
      job?.moderation_comment?.includes('Auto-approved')
    ) {
      return 'Published';
    }
    if (status === 'expired') {
      return 'Expired';
    }
    return status || 'unknown';
  };

  const isExpiringSoon = (job) => {
    const deadline = job?.application_deadline || job?.expiresAt;
    if (!deadline) return false;
    const expiryDate = new Date(deadline);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 3 && daysUntilExpiry >= 0;
  };

  const isExpired = (job) => {
    return job?.status === 'expired';
  };

  if (!jobs?.length) {
    return (
      <div className="text-center py-12">
        <Icon name="Briefcase" size={48} className="text-text-muted mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-text-primary mb-2">No Jobs Posted Yet</h3>
        <p className="text-text-secondary mb-4">Start by creating your first job posting to attract talented VAs.</p>
        <Button variant="primary" iconName="Plus" onClick={onCreateJob}>
          Post Your First Job
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header with Create Job Button */}
      {jobs?.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Job Listings</h2>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              <span className="text-sm text-text-secondary">
                {jobs.length} total job{jobs.length !== 1 ? 's' : ''}
              </span>
              <span className="text-sm text-success">
                {jobs.filter(j => j.status === 'published').length} active
              </span>
              <span className="text-sm text-warning">
                {jobs.filter(j => j.status === 'closed').length} closed
              </span>
              <span className="text-sm text-red-500">
                {jobs.filter(j => j.status === 'expired').length} expired
              </span>
              <span className="text-sm text-text-muted">
                {jobs.filter(j => j.status === 'draft').length} draft{jobs.filter(j => j.status === 'draft').length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <Button
            variant="primary"
            iconName="Plus"
            onClick={onCreateJob}
            disabled={loading}
            className="w-full sm:w-auto"
          >
            New Job Post
          </Button>
        </div>
      )}

      {/* Filters and Controls */}
      <div className="bg-surface rounded-lg border border-border p-4">
        <div className="flex flex-col space-y-4">
          {/* Search */}
          <div className="w-full">
            <div className="relative">
              <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" />
              <Input
                type="text"
                placeholder="Search jobs by title, department..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 min-w-[120px] px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Status</option>
              <option value="published">Active</option>
              <option value="closed">Closed</option>
              <option value="draft">Draft</option>
              <option value="expired">Expired</option>
              <option value="filled">Filled</option>
              <option value="archived">Archived</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="flex-1 min-w-[120px] px-3 py-2 border border-border rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="applications">Most Applications</option>
              <option value="title">Alphabetical</option>
              <option value="expiring">Expiring Soon</option>
            </select>

            {selectedJobs.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="w-full sm:w-auto mt-2 sm:mt-0"
              >
                Bulk Actions ({selectedJobs.length})
              </Button>
            )}
          </div>
        </div>

        {/* Bulk Actions */}
        {showBulkActions && selectedJobs.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <span className="text-sm text-text-secondary">
                {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => handleBulkAction('activate')}
                  className="flex-1 sm:flex-none"
                >
                  Activate
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('close')}
                  className="flex-1 sm:flex-none"
                >
                  Close
                </Button>
                <Button
                  variant="error"
                  size="sm"
                  onClick={() => handleBulkAction('delete')}
                  className="flex-1 sm:flex-none"
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Jobs Table/Cards View */}
      <div className="bg-surface rounded-lg border border-border">
        {/* View Toggle */}
        <div className="bg-secondary-50 px-6 py-3 border-b border-border flex justify-between items-center">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
              onChange={handleSelectAll}
              className="rounded border-secondary-300 text-primary focus:ring-primary mr-4"
            />
            <span className="text-sm font-medium">
              {selectedJobs.length > 0 ? 
                `${selectedJobs.length} selected` : 
                `${paginatedJobs.length} of ${filteredJobs.length} jobs`
              }
            </span>
          </div>
        </div>
        
        {/* Table View */}
        {viewMode === 'table' && (
          <>
            {/* Table Header - Hidden on Mobile */}
            <div className="hidden md:flex bg-secondary-50 px-6 py-3 border-b border-border">
              <div className="grid grid-cols-12 gap-4 flex-1 text-sm font-medium text-text-primary">
                <div className="col-span-3">Job Title</div>
                <div className="col-span-2 text-center">Status</div>
                <div className="col-span-2 text-center">Applications</div>
                <div className="col-span-2 text-center">Expires</div>
                <div className="col-span-3 text-center">Actions</div>
              </div>
            </div>

            {/* Jobs List - Desktop View (Table) */}
            <div className="hidden md:block">
              <table className="w-full">
            <tbody>
              {paginatedJobs.map((job) => (
                <tr
                  key={`table-${job.id}`}
                  className={`
                    border-b border-border hover:bg-surface-hover transition-colors 
                    ${selectedJobs.includes(job.id) ? 'bg-primary-50' : ''}
                    ${animatedJobIds.includes(job.id) ? 'animate-row-highlight' : ''}
                  `}
                >
                  <td className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selectedJobs.includes(job.id)}
                      onChange={() => handleSelectJob(job.id)}
                      className="rounded border-secondary-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-4 py-3 w-1/3">
                    <div>
                      <h3 onClick={() => handleJobTitleClick(job)} className="text-sm font-semibold text-text-primary hover:text-primary cursor-pointer">
                        {job.title || 'Untitled Job'}
                      </h3>
                      <p className="text-xs text-text-secondary">
                        {job.department || job.company_name || 'Individual'}
                      </p>
                      {isExpired(job) && (
                        <div className="flex items-center mt-1">
                          <Icon name="Clock" size={12} className="text-red-500 mr-1" />
                          <span className="text-xs text-red-500">Expired</span>
                        </div>
                      )}
                      {!isExpired(job) && isExpiringSoon(job) && (
                        <div className="flex items-center mt-1">
                          <Icon name="AlertTriangle" size={12} className="text-warning mr-1" />
                          <span className="text-xs text-warning">Expires soon</span>
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 text-center w-1/6">
                    <div className="flex items-center space-x-2 justify-center">
                      <Icon
                        name={getStatusIcon(job.status, job.moderation_status)}
                        size={16}
                        className={getStatusColor(job.status, job.moderation_status).split(' ')[1]}
                      />
                      <Badge className="capitalize" variant={getStatusVariant(job.status, job.moderation_status)} size="sm">
                        {getDisplayStatus(job.status, job.moderation_status, job)}
                      </Badge>
                    </div>
                  </td>

                  {/* Applications */}
                  <td className="px-4 py-3 text-center w-1/6">
                    <div className="flex justify-center items-center">
                      <span className={`font-medium ${animatedCountIds.includes(job.id) ? 'animate-count-update' : ''}`}>
                        {job.applications_count || job.application_count || 0}
                      </span>
                      {job.newApplications > 0 && (
                        <Badge variant="primary" size="sm" className="ml-1" animate={true}>
                          +{job.newApplications}
                        </Badge>
                      )}
                    </div>
                  </td>

                  {/* Expires */}
                  <td className="px-4 py-3 text-center w-1/6">
                    <p className={`text-sm ${isExpired(job) ? 'text-red-600 font-medium' : 'text-text-primary'}`}>
                      {job?.application_deadline ? new Date(job.application_deadline).toLocaleDateString() :
                      job?.expiresAt ? new Date(job.expiresAt).toLocaleDateString() :
                      'No expiry'}
                      {isExpired(job) && <span className="block text-xs text-red-500">Deadline passed</span>}
                    </p>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right w-1/6">
                    <div className="flex items-center space-x-1 justify-end">
                      {/* Quick Status Toggle */}
                      {job?.status === 'published' || job?.status === 'active' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Pause"
                          onClick={() => handleStatusToggle(job?.id, 'closed')}
                          disabled={actionLoading === job?.id}
                        />
                      ) : job?.status === 'closed' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Play"
                          onClick={() => handleStatusToggle(job?.id, 'published')}
                          title="Reopen Job"
                          disabled={actionLoading === job?.id}
                        />
                      ) : job?.status === 'draft' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Send"
                          onClick={() => handleStatusToggle(job?.id, 'published')}
                          title="Publish Job"
                          disabled={actionLoading === job?.id}
                        />
                      ) : job?.status === 'expired' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="CalendarPlus"
                          onClick={() => onEditJob?.(job?.id)}
                          title="Extend Deadline"
                          disabled={actionLoading === job?.id}
                        />
                      ) : job?.status === 'archived' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="RefreshCw"
                          onClick={() => handleStatusToggle(job?.id, 'published')}
                          title="Restore Job"
                          disabled={actionLoading === job?.id}
                        />
                      ) : null}
                      
                      {/* <Button
                        variant="ghost"
                        size="sm"
                        iconName="Eye"
                        onClick={() => {
                          // Reset new applications counter when viewing applications
                          setJobsState(prevJobs => prevJobs.map(j => 
                            j.id === job?.id ? { ...j, newApplications: 0 } : j
                          ));
                          onViewApplications?.(job?.id);
                        }}
                        title="View Applications"
                        disabled={actionLoading === job?.id}
                      /> */}

                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={actionLoading === job?.id && currentAction === 'clone' ? "Loader" : "Copy"}
                        onClick={() => handleDropdownAction('clone', job?.id)}
                        title="Clone Job"
                        disabled={actionLoading === job?.id}
                      />

                      <Button
                        variant="ghost"
                        size="sm"
                        iconName={actionLoading === job?.id && currentAction === 'delete' ? "Loader" : "Trash2"}
                        onClick={() => handleDropdownAction('delete', job?.id)}
                        title="Delete Job"
                        disabled={actionLoading === job?.id}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
            </>
          )}

          {/* Grid View */}
          {viewMode === 'grid' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-6">
              {paginatedJobs.map((job) => (
                <div
                  key={`grid-${job.id}`}
                  className={`
                    border border-border rounded-lg p-4 hover:shadow-md transition-all
                    ${selectedJobs.includes(job.id) ? 'bg-primary-50 border-primary' : 'bg-surface hover:bg-surface-hover'}
                    ${animatedJobIds.includes(job.id) ? 'animate-row-highlight' : ''}
                  `}
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 onClick={() => handleJobTitleClick(job)} className="text-sm font-semibold text-text-primary hover:text-primary cursor-pointer line-clamp-2">
                        {job.title || 'Untitled Job'}
                      </h3>
                      <p className="text-xs text-text-secondary mt-1">
                        {job.company_name || job.department || 'Individual'}
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={selectedJobs.includes(job.id)}
                      onChange={() => handleSelectJob(job.id)}
                      className="rounded border-secondary-300 text-primary focus:ring-primary mt-1"
                    />
                  </div>

                  {/* Status Badge */}
                  <div className="mb-3">
                    <Badge className="capitalize" variant={getStatusVariant(job.status, job.moderation_status)} size="sm">
                      {getDisplayStatus(job.status, job.moderation_status, job)}
                    </Badge>
                  </div>

                  {/* Job Details */}
                  <div className="space-y-2 mb-4 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Applications:</span>
                      <span className={`font-medium ${animatedCountIds.includes(job.id) ? 'animate-count-update' : ''}`}>
                        {job.applications_count || job.application_count || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Location:</span>
                      <span className="font-medium text-xs truncate">{job.location || 'Not specified'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Type:</span>
                      <span className="font-medium text-xs capitalize">{job.job_type || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-text-secondary">Expires:</span>
                      <span className={`font-medium text-xs ${isExpired(job) ? 'text-red-600' : ''}`}>
                        {job?.application_deadline ? new Date(job.application_deadline).toLocaleDateString() :
                        job?.expiresAt ? new Date(job.expiresAt).toLocaleDateString() : 'No expiry'}
                        {isExpired(job) && ' (Expired)'}
                      </span>
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => onViewApplications?.(job?.id)}
                      className="w-full"
                    >
                      View Applications
                    </Button>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Edit2"
                        onClick={() => onEditJob?.(job?.id)}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Copy"
                        onClick={() => onDuplicateJob?.(job?.id)}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Trash2"
                        onClick={() => {
                          setJobToDelete(job?.id);
                          setShowDeleteConfirm(true);
                        }}
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        {/* Jobs List - Mobile View (Cards) */}
        <div className="md:hidden">
          {paginatedJobs.map((job) => (
            <div 
              key={`card-${job.id}`}
              className={`
                border-b border-border p-4 hover:bg-surface-hover transition-colors
                ${selectedJobs.includes(job.id) ? 'bg-primary-50' : ''}
                ${animatedJobIds.includes(job.id) ? 'animate-row-highlight' : ''}
              `}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedJobs.includes(job.id)}
                    onChange={() => handleSelectJob(job.id)}
                    className="rounded border-secondary-300 text-primary focus:ring-primary mt-1"
                  />
                  <div>
                    <h3 onClick={() => handleJobTitleClick(job)} className="text-sm font-semibold text-text-primary hover:text-primary cursor-pointer">
                      {job.title || 'Untitled Job'}
                    </h3>
                    <p className="text-xs text-text-secondary">
                      {job.department || job.company_name || 'Individual'}
                    </p>
                  </div>
                </div>
                <Badge className="capitalize" variant={getStatusVariant(job.status, job.moderation_status)} size="sm">
                  {getDisplayStatus(job.status, job.moderation_status, job)}
                </Badge>
              </div>
              
              {/* Job Details */}
              <div className="mt-3 pl-8 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <div className="text-text-secondary text-xs">Applications</div>
                  <div className="flex items-center">
                    <span className={`font-medium ${animatedCountIds.includes(job.id) ? 'animate-count-update' : ''}`}>
                      {job.applications_count || job.application_count || 0}
                    </span>
                    {job.newApplications > 0 && (
                      <Badge variant="primary" size="sm" className="ml-1" animate={true}>
                        +{job.newApplications}
                      </Badge>
                    )}
                  </div>
                </div>
                
                <div>
                  <div className="text-text-secondary text-xs">Expires</div>
                  <div>
                    {job?.application_deadline ? new Date(job.application_deadline).toLocaleDateString() :
                    job?.expiresAt ? new Date(job.expiresAt).toLocaleDateString() :
                    'No expiry'}
                    {isExpired(job) && (
                      <div className="flex items-center mt-1">
                        <Icon name="Clock" size={12} className="text-red-500 mr-1" />
                        <span className="text-xs text-red-500">Expired</span>
                      </div>
                    )}
                    {!isExpired(job) && isExpiringSoon(job) && (
                      <div className="flex items-center mt-1">
                        <Icon name="AlertTriangle" size={12} className="text-warning mr-1" />
                        <span className="text-xs text-warning">Expires soon</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="mt-3 pl-8 flex items-center justify-between border-t border-border pt-3">
                <div className="flex space-x-1">
                  {/* Quick Status Toggle */}
                  {job?.status === 'published' || job?.status === 'active' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Pause"
                      onClick={() => handleStatusToggle(job?.id, 'closed')}
                      title="Close Job"
                      disabled={actionLoading === job?.id}
                    >
                      Close
                    </Button>
                  ) : job?.status === 'closed' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Play"
                      onClick={() => handleStatusToggle(job?.id, 'published')}
                      title="Reopen Job"
                      disabled={actionLoading === job?.id}
                    >
                      Reopen
                    </Button>
                  ) : job?.status === 'draft' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Send"
                      onClick={() => handleStatusToggle(job?.id, 'published')}
                      title="Publish Job"
                      disabled={actionLoading === job?.id}
                    >
                      Publish
                    </Button>
                  ) : job?.status === 'archived' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="RefreshCw"
                      onClick={() => handleStatusToggle(job?.id, 'published')}
                      title="Restore Job"
                      disabled={actionLoading === job?.id}
                    >
                      Restore
                    </Button>
                  ) : job?.status === 'expired' ? (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="CalendarPlus"
                      onClick={() => onEditJob?.(job?.id)}
                      title="Extend Deadline"
                      disabled={actionLoading === job?.id}
                    >
                      Extend
                    </Button>
                  ) : null}
                </div>
                
                <div className="flex space-x-1">
                  <Button
                    variant="primary"
                    size="sm"
                    iconName="Eye"
                    onClick={() => {
                      // Reset new applications counter when viewing applications
                      setJobsState(prevJobs => prevJobs.map(j => 
                        j.id === job?.id ? { ...j, newApplications: 0 } : j
                      ));
                      onViewApplications?.(job?.id);
                    }}
                    title="View Applications"
                    disabled={actionLoading === job?.id}
                  >
                  </Button>

                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={actionLoading === job?.id && currentAction === 'clone' ? "Loader" : "Copy"}
                    onClick={() => handleDropdownAction('clone', job?.id)}
                    title="Clone Job"
                    disabled={actionLoading === job?.id}
                  />

                  <Button
                    variant="ghost"
                    size="sm"
                    iconName={actionLoading === job?.id && currentAction === 'delete' ? "Loader" : "Trash2"}
                    onClick={() => handleDropdownAction('delete', job?.id)}
                    title="Delete Job"
                    disabled={actionLoading === job?.id}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredJobs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary mb-2">No Jobs Found</h3>
            <p className="text-text-secondary">
              Try adjusting your search criteria or filters to find more jobs.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
              }}
              className="mt-4"
            >
              Clear Filters
            </Button>
          </div>
        )}
        
        {/* Bulk Confirmation Dialog */}
        {showBulkConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                Confirm Bulk Action
              </h3>
              <p className="text-text-secondary mb-6">
                Are you sure you want to {pendingBulkAction} {selectedJobs.length} job{selectedJobs.length !== 1 ? 's' : ''}?
                {pendingBulkAction === 'delete' && ' This action cannot be undone.'}
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkConfirm(false);
                    setPendingBulkAction(null);
                  }}
                  disabled={actionLoading === 'bulk'}
                >
                  Cancel
                </Button>
                <Button
                  variant={pendingBulkAction === 'delete' ? 'error' : 'primary'}
                  onClick={confirmBulkAction}
                  disabled={actionLoading === 'bulk'}
                  iconName={actionLoading === 'bulk' ? "Loader2" : undefined}
                >
                  {actionLoading === 'bulk' ? 'Processing...' : (pendingBulkAction === 'delete' ? 'Delete' : 'Confirm')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Job Confirmation Dialog */}
        {showDeleteConfirm && jobToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <Icon name="AlertTriangle" size={24} className="text-error mr-3" />
                <h3 className="text-lg font-semibold text-text-primary">
                  Delete Job
                </h3>
              </div>
              <p className="text-text-secondary mb-6">
                Are you sure you want to delete this job? This action cannot be undone and will permanently remove the job listing and all associated applications.
              </p>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setJobToDelete(null);
                  }}
                  disabled={actionLoading === jobToDelete}
                >
                  Cancel
                </Button>
                <Button
                  variant="error"
                  onClick={confirmDeleteJob}
                  disabled={actionLoading === jobToDelete}
                  iconName={actionLoading === jobToDelete ? "Loader2" : "Trash2"}
                >
                  {actionLoading === jobToDelete ? 'Deleting...' : 'Delete Job'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table Footer */}
      {filteredJobs.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 text-sm text-text-secondary p-4">
          <span className="text-center sm:text-left">
            Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredJobs.length)} to {Math.min(currentPage * itemsPerPage, filteredJobs.length)} of {filteredJobs.length} jobs
          </span>
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-2">
              <span>Jobs per page:</span>
              <select 
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="w-14 px-2 py-1 border border-border rounded text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center w-full sm:w-auto gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                  className="px-2"
                />
                <span className="px-2 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                  className="px-2"
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upgrade CTA Prompt */}
      {userProfile?.subscription_tier === 'free' && (
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="w-full sm:max-w-md">
              <h3 className="text-lg font-semibold text-text-primary mb-1">
                Unlock More Features
              </h3>
              <p className="text-sm text-text-secondary">
                Upgrade your plan to post more jobs, access more matches, hire more candidates, instantly message them, and receive priority support.
              </p>
            </div>
            <Button
              variant="primary"
              size="md"
              className="w-full sm:w-auto"
              onClick={() => window.location.href = '/pricing'}
            >
              Upgrade Plan
            </Button>
          </div>
        </div>
      )}
    </div>

    {toast.show && (
      <Toast
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(t => ({ ...t, show: false }))}
        autoClose={5000}
      />
    )}
    </>
  );
};

export default JobsTable;
